'use client'

// libraries
import clsx from 'clsx'
import { useState, useCallback } from 'react'

// components
import Section from '/react4xp/components/Section'
import RoundedBox from '/react4xp/components/RoundedBox'
import PublicationCard from '/react4xp/components/PublicationCard'
import { Add, Loading } from '/react4xp/components/Icons/Ux'
import Accordion, { AccordionList } from '../Accordion'

// css
import './index.scss'

// types
export interface PublicationsProps {
    className?: string
    isAnimated?: boolean
    title?: string
    color?: 'white' | 'green-light' | 'beige'
    cardStyle?: 'link' | 'accordion'
    publications?: {
        href?: {
            href?: string
            external?: boolean
        }
        title?: string
        intro?: string
        type?: string
    }[]
    publicationGroups?: {
        title?: string
        items?: {
            href?: {
                href?: string
                external?: boolean
            }
            title?: string
            intro?: string
        }[]
        groupType?: 'scientific' | 'other'
        total?: number
        hasNextPage?: boolean
        serviceUrl?: string
        employeeId?: string
        initialCount?: number
        loadMoreCount?: number
    }[]
    howManyPerPage?: number
    loadMoreLabel?: string
    initialCount?: number
    loadMoreCount?: number
}


export default function Publications({
    className,
    isAnimated,
    title,
    color,
    cardStyle = 'link',
    publications = [],
    publicationGroups = [],
    howManyPerPage = 10,
    loadMoreLabel = 'Load more',
    initialCount = 5,
    loadMoreCount = 10,
}: PublicationsProps) {
    const [currentPage, setCurrentPage] = useState(1)
    const [loadMore, setLoadMore] = useState(false)
    const [accordionAppendedItems, setAccordionAppendedItems] = useState<Record<number, Array<{ title?: string; href?: { href?: string; external?: boolean }; intro?: string }>>>({})
    const [accordionHasNextPage, setAccordionHasNextPage] = useState<Record<number, boolean>>({})
    const [accordionLoading, setAccordionLoading] = useState<Record<number, boolean>>({})

    const displayedPublications = publications.slice(0, currentPage * howManyPerPage)
    const hasNextPage = publications.length > currentPage * howManyPerPage

    const handleLoadMore = () => {
        if (hasNextPage) {
            setLoadMore(true)
            setTimeout(() => {
                setCurrentPage(prev => prev + 1)
                setLoadMore(false)
            }, 300)
        }
    }

    const handleAccordionLoadMore = useCallback(
        async (groupIndex: number) => {
            const group = publicationGroups[groupIndex]
            if (!group?.serviceUrl || !group?.employeeId || !group?.groupType || accordionLoading[groupIndex]) {
                return
            }

            const currentItems = group.items ?? []
            const appended = accordionAppendedItems[groupIndex] ?? []
            const start = currentItems.length + appended.length
            const count = group.loadMoreCount ?? 10

            setAccordionLoading(prev => ({ ...prev, [groupIndex]: true }))

            try {
                const url = new URL(group.serviceUrl)
                url.searchParams.set('employeeId', group.employeeId)
                url.searchParams.set('groupType', group.groupType)
                url.searchParams.set('start', String(start))
                url.searchParams.set('count', String(count))

                const res = await fetch(url.toString())
                const data = await res.json()

                if (data.hits?.length) {
                    setAccordionAppendedItems(prev => ({
                        ...prev,
                        [groupIndex]: [...(prev[groupIndex] ?? []), ...data.hits]
                    }))
                }
                setAccordionHasNextPage(prev => ({
                    ...prev,
                    [groupIndex]: data.hasNextPage ?? false
                }))
            } finally {
                setAccordionLoading(prev => ({ ...prev, [groupIndex]: false }))
            }
        },
        [publicationGroups, accordionLoading, accordionAppendedItems]
    )

    return (
        <Section className={clsx(
            'publications-component',
            className
        )}>
            <div
                className='container'
                data-stagger-up={isAnimated ? 'true' : 'false'}
            >
                {title && (
                    <RoundedBox
                        bgColor={color}
                        className='publications-component__box'
                    >
                        <div className='row'>
                            <div className='col-lg-10 offset-lg-1 col-xl-6 offset-xl-3'>
                                <div className='publications-component__flex'>

                                    {title && (
                                        <h2 className='h2 publications-component__title'>
                                            {title}
                                        </h2>
                                    )}

                                    {(publications.length > 0 || publicationGroups.length > 0) && (
                                        <>
                                            {cardStyle === 'link' && (
                                                <div className='publications-component__publications'>
                                                    {displayedPublications.map((item, i) => (
                                                        <PublicationCard
                                                            key={i}
                                                            href={item.href}
                                                            title={item.title}
                                                            intro={item.intro}
                                                        />
                                                    ))}
                                                </div>
                                            )}

                                            {cardStyle === 'accordion' && (
                                                <AccordionList oneAtTime>
                                                    <div className='accordions-component__accordions'>
                                                        {
                                                            publicationGroups.map((publicationGroup, groupIndex) => {
                                                                const groupItems = publicationGroup.items ?? []
                                                                const appendedItems = accordionAppendedItems[groupIndex] ?? []
                                                                const displayedItems = [...groupItems, ...appendedItems]
                                                                const hasMoreInAccordion = accordionHasNextPage[groupIndex] ?? publicationGroup.hasNextPage ?? false
                                                                const isLoading = accordionLoading[groupIndex] ?? false

                                                                return (
                                                                    <Accordion
                                                                        key={publicationGroup.title}
                                                                        anchorId={publicationGroup.title}
                                                                        title={publicationGroup.title}
                                                                        defaultOpen={publicationGroups.length === 1}
                                                                        content={
                                                                            <div className='publications-component__accordion-content'>
                                                                                {displayedItems.map((pub, i) => (
                                                                                    <div key={i} className='publications-component__accordion-item'>
                                                                                        <a
                                                                                            href={pub.href?.href}
                                                                                            target={pub.href?.external ? '_blank' : undefined}
                                                                                            rel={pub.href?.external ? 'noopener noreferrer' : undefined}
                                                                                        >
                                                                                            {pub.title && (
                                                                                                <h3 className='h4'>
                                                                                                    {pub.title}
                                                                                                </h3>
                                                                                            )}
                                                                                            {pub.intro && (
                                                                                                <p className='body'>
                                                                                                    {pub.intro}
                                                                                                </p>
                                                                                            )}
                                                                                        </a>
                                                                                    </div>
                                                                                ))}
                                                                                {hasMoreInAccordion && (
                                                                                    <div className='publications-component__accordion-load-more'>
                                                                                        <button
                                                                                            className='button-hollow-black'
                                                                                            type='button'
                                                                                            onClick={() => handleAccordionLoadMore(groupIndex)}
                                                                                            disabled={isLoading}
                                                                                        >
                                                                                            <Add />
                                                                                            <span className='button-hollow-black__text'>
                                                                                                {loadMoreLabel}
                                                                                            </span>
                                                                                            {isLoading && (
                                                                                                <span className='button-hollow-black__loading'>
                                                                                                    <Loading />
                                                                                                </span>
                                                                                            )}
                                                                                        </button>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        }
                                                                        color={
                                                                            color === 'white' || color === 'beige' ? 'green' :
                                                                            color === 'green-light' ? 'beige' :
                                                                            'green'
                                                                        }
                                                                    />
                                                                )
                                                            })
                                                        }
                                                    </div>
                                                </AccordionList>
                                            )}
                                        </>
                                    )}

                                    {(hasNextPage && cardStyle != 'accordion') && (
                                        <div className='publications-component__load-more'>
                                            <button
                                                type='button'
                                                className='cta-button rounded-box-component'
                                                disabled={loadMore}
                                                onClick={handleLoadMore}
                                                tabIndex={loadMore ? -1 : 0}
                                            >
                                                <Add />
                                                {loadMoreLabel}

                                                {loadMore && (
                                                    <span className='publications-component__load-more__loading'>
                                                        <Loading />
                                                    </span>
                                                )}
                                            </button>
                                        </div>
                                    )}

                                </div>

                            </div>
                        </div>
                    </RoundedBox>
                )}
            </div>
        </Section>
    )
}
